import {Component, DestroyRef, inject, OnDestroy, OnInit} from "@angular/core";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {CommonModule} from "@angular/common";
import {MatDialog, MatDialogModule} from "@angular/material/dialog";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {MatSelectModule} from "@angular/material/select";
import {MatTooltipModule} from "@angular/material/tooltip";
import {Store} from "@ngrx/store";

import {FundActions} from "../../store/fund.actions";
import {selectFundContext, selectMergePreviewData, selectMergePreviewLoading} from "../../store/fund/fund.selectors";
import {FundService} from "../../services/fund.service";
import {selectSelectedSourceDataset, selectSelectedTargetDataset} from "../../../import/store/import.selectors";
import {FundApiService, MergePreviewEntity} from "../../generated-sources/api";
import {AssetApiService} from "../../../asset/generated-sources/api";
import {ErrorHandlingUtil} from "../../../shared/utils/error-handling-util";
import {SharedModule} from "../../../shared/shared.module";
import {MergeSearchDialogComponent} from "./merge-search-dialog.component";

@Component({
    selector: "valumize-merge-preview-dialog",
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatSelectModule,
        MatTooltipModule,
        SharedModule,
    ],
    templateUrl: "./merge-preview-dialog.component.html",
    styleUrl: "./merge-preview-dialog.component.scss",
})
export class MergePreviewDialogComponent implements OnInit, OnDestroy {
    private readonly store = inject(Store);
    private readonly dialog = inject(MatDialog);
    private readonly fundApiService = inject(FundApiService);
    private readonly fundService = inject(FundService);
    private readonly assetApiService = inject(AssetApiService);
    private readonly destroyRef = inject(DestroyRef);

    preview$ = this.store.select(selectMergePreviewData);
    isLoading$ = this.store.select(selectMergePreviewLoading);
    sourceDataset$ = this.store.select(selectSelectedSourceDataset);
    targetDataset$ = this.store.select(selectSelectedTargetDataset);

    isAutoMatching = false;
    targetChangeError = "";

    private sourceDataset = "";
    private targetDataset: string | undefined;
    private fundSourceId: number | undefined;
    private fundReportId: number | undefined;

    ngOnInit(): void {
        this.store.dispatch(FundActions.loadMergePreview());

        this.sourceDataset$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(ds => this.sourceDataset = ds);

        this.targetDataset$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(ds => this.targetDataset = ds);

        this.store.select(selectFundContext)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(ctx => {
                this.fundSourceId = ctx.fundId;
                this.fundReportId = ctx.fundReportId;
            });
    }

    ngOnDestroy(): void {
        this.store.dispatch(FundActions.clearMergePreview());
    }

    openFundSearch(): void {
        const ref = this.dialog.open(MergeSearchDialogComponent, {
            autoFocus: true,
            data: {categories: ["funds"]}
        });
        ref.afterClosed().subscribe(result => {
            if (result) this.selectFundMergeTarget(result);
        });
    }

    openAssetSearch(asset: MergePreviewEntity): void {
        const ref = this.dialog.open(MergeSearchDialogComponent, {
            autoFocus: true,
            data: {categories: ["assets"]}
        });
        ref.afterClosed().subscribe(result => {
            if (result) this.selectAssetMergeTarget(asset, result);
        });
    }

    private selectFundMergeTarget(searchResultReference: string): void {
        const match = RegExp(/^\/funds\/(\d+)$/).exec(searchResultReference);
        if (!match || !this.fundSourceId) return;
        const mergeTargetId = Number(match[1]);
        this.fundApiService.setMergeTargetFund(this.fundSourceId, this.fundReportId, mergeTargetId, this.sourceDataset)
            .subscribe({
                next: (fundData) => {
                    this.targetChangeError = "";
                    this.store.dispatch(FundActions.setMergeTargetSuccess({fundData}));
                    this.store.dispatch(FundActions.loadMergePreview());
                },
                error: (err) => this.targetChangeError = ErrorHandlingUtil.getErrorMessage(err)
            });
    }

    clearFundMergeTarget(): void {
        if (!this.fundSourceId) return;
        this.fundApiService.setMergeTargetFund(this.fundSourceId, this.fundReportId, undefined, this.sourceDataset)
            .subscribe({
                next: (fundData) => {
                    this.targetChangeError = "";
                    this.store.dispatch(FundActions.setMergeTargetSuccess({fundData}));
                    this.store.dispatch(FundActions.loadMergePreview());
                },
                error: (err) => this.targetChangeError = ErrorHandlingUtil.getErrorMessage(err)
            });
    }

    private selectAssetMergeTarget(asset: MergePreviewEntity, searchResultReference: string): void {
        if (!asset.sourceId) return;
        const match = RegExp(/^\/assets\/(\d+)$/).exec(searchResultReference);
        if (!match) return;
        const mergeTargetId = Number(match[1]);
        this.assetApiService.setMergeTargetAsset(asset.sourceId, undefined, mergeTargetId, this.sourceDataset)
            .subscribe({
                next: () => {
                    this.targetChangeError = "";
                    this.store.dispatch(FundActions.loadMergePreview());
                },
                error: (err) => this.targetChangeError = ErrorHandlingUtil.getErrorMessage(err)
            });
    }

    clearAssetMergeTarget(asset: MergePreviewEntity): void {
        if (!asset.sourceId) return;
        this.assetApiService.setMergeTargetAsset(asset.sourceId, undefined, undefined, this.sourceDataset)
            .subscribe({
                next: () => {
                    this.targetChangeError = "";
                    this.store.dispatch(FundActions.loadMergePreview());
                },
                error: (err) => this.targetChangeError = ErrorHandlingUtil.getErrorMessage(err)
            });
    }

    autoMatchAssets(): void {
        if (!this.fundSourceId) return;
        this.isAutoMatching = true;
        this.fundApiService.autoMatchAssets(this.fundSourceId, this.sourceDataset, this.targetDataset)
            .subscribe({
                next: (preview) => {
                    this.isAutoMatching = false;
                    this.targetChangeError = "";
                    this.store.dispatch(FundActions.loadMergePreviewSuccess({preview}));
                    // Sync sidebar by fetching fresh FundData
                    this.refreshSidebarFundData();
                },
                error: (err) => {
                    this.isAutoMatching = false;
                    this.targetChangeError = ErrorHandlingUtil.getErrorMessage(err);
                }
            });
    }

    private refreshSidebarFundData(): void {
        if (!this.fundSourceId) return;
        this.fundService.getFundData(this.fundSourceId, this.fundReportId, undefined, undefined, this.sourceDataset)
            .subscribe(fundData => this.store.dispatch(FundActions.setMergeTargetSuccess({fundData})));
    }
}
